export type GuideHeading = {
  id: string;
  text: string;
  level: 2 | 3;
};

export type PreparedGuide = {
  html: string;
  headings: GuideHeading[];
};

const WORDS_PER_MINUTE = 220;

function stripTags(html: string): string {
  return html.replace(/<[^>]+>/g, " ").replace(/&amp;/g, "&").replace(/\s+/g, " ").trim();
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/[\s-]+/g, "-");
}

/** Adds stable ids to h2/h3 headings so the page can link a table of contents to them. */
export function prepareGuide(html: string): PreparedGuide {
  const headings: GuideHeading[] = [];
  const seen = new Map<string, number>();
  const prepared = html.replace(
    /<(h[23])([^>]*)>([\s\S]*?)<\/\1>/g,
    (_match, tag: string, attributes: string, inner: string) => {
      const text = stripTags(inner);
      const base = slugify(text) || "section";
      const count = seen.get(base) ?? 0;
      seen.set(base, count + 1);
      const id = count === 0 ? base : `${base}-${count + 1}`;
      headings.push({ id, text, level: tag === "h2" ? 2 : 3 });
      return `<${tag}${attributes} id="${id}">${inner}</${tag}>`;
    },
  );
  return { html: prepared, headings };
}

export function readingMinutes(html: string): number {
  const words = stripTags(html).split(" ").filter(Boolean).length;
  return Math.max(1, Math.round(words / WORDS_PER_MINUTE));
}

export function formatDate(value: string, style: "short" | "long" = "long"): string {
  return new Intl.DateTimeFormat("en-US", {
    month: style === "long" ? "long" : "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(value));
}
